import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Crown, BadgeCheck, Sparkles, TrendingUp, ArrowRight, Check, Loader2, ShieldCheck, Flame, Gift } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const CURRENCY_SYMBOL = { gbp: "£", usd: "$", eur: "€", cad: "C$" };

const Benefit = ({ icon: Icon, title, sub }) => (
  <div className="flex gap-3 items-start">
    <div className="w-9 h-9 rounded-md bg-[#C05A3C]/10 flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-[#C05A3C]"/>
    </div>
    <div>
      <div className="font-semibold text-sm">{title}</div>
      <div className="text-sm text-muted-foreground">{sub}</div>
    </div>
  </div>
);

const Pro = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [pub, setPub] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [credit, setCredit] = useState(0);

  useEffect(() => {
    api.get("/subscription/pro/public").then((r) => setPub(r.data));
    if (user) {
      api.get("/subscription/pro/status").then((r) => setStatus(r.data));
      api.get("/me/credit-balance?currency=GBP").then((r) => setCredit(r.data.balance || 0)).catch(() => {});
    }
  }, [user]);

  const redeemWithCredits = async () => {
    if (!user) { navigate("/login", { state: { from: "/pro" } }); return; }
    setRedeeming(true);
    try {
      await api.post("/payments/redeem-credits", { target: "pro" });
      toast.success("Pro activated using your referral credits!");
      if (refresh) await refresh();
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not redeem");
    } finally { setRedeeming(false); }
  };

  const startCheckout = async () => {
    if (!user) { navigate("/login", { state: { from: "/pro" } }); return; }
    if (user.verification_status !== "approved") {
      toast.error("Complete KYC verification first");
      navigate("/verify");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/pro", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  const isPro = status?.is_pro;
  const isFounder = status?.is_pro_founder;
  const userPrice = status?.price ?? pub?.founder_price ?? 4.99;
  const regularPrice = pub?.regular_price ?? 9.99;
  const founderPrice = pub?.founder_price ?? 4.99;
  const founderSlots = pub?.founders_remaining ?? 0;
  const totalFounderSlots = pub?.founders_limit ?? 100;
  const currency = pub?.currency || status?.currency || "gbp";
  const sym = CURRENCY_SYMBOL[currency] || currency.toUpperCase() + " ";
  const founderDiscountPct = Math.round(100 - (founderPrice / regularPrice) * 100);
  const founderActive = founderSlots > 0 || isFounder;

  return (
    <div className="container-narrow py-16 max-w-5xl">
      <SEO
        title="Pro membership — unlock priority + verified badge"
        description="Upgrade to AfriMarket Pro from £4.99/mo. Priority placement, verified badge, lower commission, leaderboard ranking. Founders tier limited to first 100 sellers."
        path="/pro"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "AfriMarket Pro Membership",
          "description": "Monthly subscription for AfriMarket sellers — priority placement, verified badge, lower commission, leaderboard ranking, advanced analytics.",
          "brand": { "@type": "Brand", "name": "AfriMarket" },
          "offers": {
            "@type": "Offer",
            "url": "https://afrimarket.org/pro",
            "priceCurrency": "GBP",
            "price": "4.99",
            "availability": "https://schema.org/InStock",
          },
        }}
      />
      {/* Founders banner */}
      {founderActive && !isPro && (
        <div className="mb-10 bg-[#1A2024] text-white rounded-lg p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 relative overflow-hidden" data-testid="founders-banner">
          <div className="absolute inset-0 ankara-stripe opacity-20" aria-hidden/>
          <Flame className="w-7 h-7 text-[#D9923B] relative shrink-0"/>
          <div className="flex-1 relative">
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-[#D9923B] text-[#1A2024]">FOUNDERS OFFER</Badge>
              <span className="text-xs text-white/70">Limited to first {totalFounderSlots} sellers</span>
            </div>
            <div className="font-display text-2xl leading-tight">{sym}{founderPrice}/30 days — locked in for life</div>
            <div className="text-sm text-white/70 mt-1">Save {founderDiscountPct}% forever as one of AfriMarket&apos;s founding sellers. Only <span className="text-[#D9923B] font-semibold">{founderSlots}</span> of {totalFounderSlots} spots left.</div>
          </div>
          <div className="text-right relative">
            <div className="font-display text-4xl text-[#D9923B] leading-none">{founderSlots}</div>
            <div className="text-xs text-white/60 uppercase tracking-wider">left</div>
          </div>
        </div>
      )}
      {isFounder && (
        <div className="mb-10 bg-[#D9923B]/10 border border-[#D9923B]/30 rounded-lg p-4 flex items-center gap-3" data-testid="founder-locked-banner">
          <Flame className="w-5 h-5 text-[#D9923B]"/>
          <div className="text-sm">You&apos;re a <span className="font-semibold">founding member</span>. Locked at {sym}{founderPrice}/30 days for life — thanks for being early.</div>
        </div>
      )}
      <div className="grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-3">
          <div className="label-overline text-[#D9923B] mb-3 flex items-center gap-2">
            <Crown className="w-3.5 h-3.5"/> AfriMarket Pro
          </div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight leading-[0.95]">
            Sell smarter.<br/>
            Pay <span className="text-[#C05A3C]">less</span>. Stand out.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-xl font-sans-ui leading-relaxed">
            For serious sellers who want to grow on AfriMarket. Lower fees, verified badge,
            priority placement and a Pro mark on every listing — all for one simple monthly price.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            <Benefit
              icon={TrendingUp}
              title="5% flat fee on every sale"
              sub="Down from 8% on products and 10% on services. Earn 3–5% more on every order."
            />
            <Benefit
              icon={BadgeCheck}
              title="Verified badge included"
              sub={`The trust mark (${sym}${pub?.verified_badge_price ?? 19.99} if bought separately) comes bundled with Pro.`}
            />
            <Benefit
              icon={Sparkles}
              title="Priority placement"
              sub="Your listings appear above non-Pro listings on Browse and the homepage."
            />
            <Benefit
              icon={Crown}
              title="Pro mark on every listing"
              sub="Buyers see a ✦ Pro badge across your listings, lifting click-throughs."
            />
          </div>

          <div className="mt-12 p-6 bg-[#F3EFE9] rounded-lg flex items-start gap-3 text-sm">
            <ShieldCheck className="w-5 h-5 text-[#2A3D36] shrink-0 mt-0.5"/>
            <div>
              <div className="font-semibold mb-1">Risk-free 30-day pass</div>
              <div className="text-muted-foreground">
                Each payment buys 30 days of Pro. No auto-renewal — top up whenever it suits you.
                Cancel by simply not renewing.
              </div>
            </div>
          </div>
        </div>

        {/* Pricing card */}
        <div className="lg:col-span-2 lg:sticky lg:top-24">
          <Card className="p-8 bg-[#1A2024] text-white border-transparent overflow-hidden relative">
            <div className="absolute inset-0 ankara-stripe opacity-15" aria-hidden/>
            <div className="relative">
              <Badge className="bg-[#D9923B] text-[#1A2024] mb-4">
                <Crown className="w-3 h-3 mr-1"/> {isFounder ? "FOUNDER PRO" : "PRO MEMBERSHIP"}
              </Badge>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-6xl leading-none">{sym}{userPrice.toFixed(2)}</span>
                <span className="text-sm text-white/60">/ 30 days</span>
              </div>
              {(founderActive || isFounder) && userPrice < regularPrice && (
                <div className="mt-1 text-sm">
                  <span className="line-through text-white/40 mr-2">{sym}{regularPrice.toFixed(2)}</span>
                  <span className="text-[#D9923B] font-semibold">Save {founderDiscountPct}%{isFounder ? " for life" : ""}</span>
                </div>
              )}
              <div className="mt-2 text-xs text-white/60">One-time payment via Stripe · No subscription lock-in</div>

              <ul className="mt-8 space-y-3 text-sm">
                {[
                  "5% platform fee (vs. 8–10%)",
                  `Verified badge included (${sym}${pub?.verified_badge_price ?? 19.99} value)`,
                  "Priority placement on Browse",
                  "Pro mark on every listing",
                  "Same-day listing approval",
                  isFounder ? "Founder price locked for life" : "Cancel anytime — no renewal",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#D9923B] mt-0.5 shrink-0"/>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>

              {isPro ? (
                <div className="mt-8">
                  <Badge className="bg-[#4A5D23] text-white mb-3">✓ You&apos;re a Pro member</Badge>
                  <div className="text-sm text-white/80">
                    {status.days_remaining > 0
                      ? `${status.days_remaining} day${status.days_remaining === 1 ? "" : "s"} remaining`
                      : "Renew now to keep your Pro benefits"}
                  </div>
                  <Button
                    onClick={startCheckout}
                    disabled={loading}
                    data-testid="extend-pro-btn"
                    className="mt-4 w-full bg-[#D9923B] hover:bg-[#D9923B]/90 text-[#1A2024] font-semibold h-12 rounded-md"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Extend +30 days <ArrowRight className="w-4 h-4 ml-2"/></>}
                  </Button>
                  {credit >= userPrice && (
                    <Button
                      type="button"
                      onClick={redeemWithCredits}
                      disabled={redeeming}
                      data-testid="pro-redeem-credits-btn"
                      variant="outline"
                      className="mt-2 w-full border-[#D9923B]/40 text-[#D9923B] hover:bg-[#D9923B]/10 hover:text-[#D9923B] h-10 rounded-md"
                    >
                      {redeeming ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Gift className="w-3.5 h-3.5 mr-2"/> Use {sym}{credit.toFixed(2)} credits instead</>}
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <Button
                    onClick={startCheckout}
                    disabled={loading}
                    data-testid="upgrade-pro-btn"
                    className="mt-8 w-full bg-[#D9923B] hover:bg-[#D9923B]/90 text-[#1A2024] font-semibold h-12 rounded-md"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Upgrade to Pro <ArrowRight className="w-4 h-4 ml-2"/></>}
                  </Button>
                  {credit >= userPrice && user?.verification_status === "approved" && (
                    <Button
                      type="button"
                      onClick={redeemWithCredits}
                      disabled={redeeming}
                      data-testid="pro-redeem-credits-btn"
                      variant="outline"
                      className="mt-3 w-full border-[#D9923B]/40 text-[#D9923B] hover:bg-[#D9923B]/10 hover:text-[#D9923B] h-12 rounded-md"
                    >
                      {redeeming ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Gift className="w-4 h-4 mr-2"/> Pay with referral credits ({sym}{credit.toFixed(2)} available)</>}
                    </Button>
                  )}
                  {user?.verification_status !== "approved" && user && (
                    <Link to="/verify" className="block text-xs text-center mt-3 text-white/70 hover:text-white underline">
                      Complete KYC first →
                    </Link>
                  )}
                  {!user && (
                    <Link to="/register" className="block text-xs text-center mt-3 text-white/70 hover:text-white underline">
                      Sign up free first →
                    </Link>
                  )}
                </>
              )}
            </div>
          </Card>

          <div className="mt-4 text-xs text-muted-foreground text-center">
            Questions? <Link to="/" className="underline">Read the FAQ</Link>
          </div>
        </div>
      </div>

      {/* Math example */}
      <Card className="mt-16 p-8 bg-white border-black/10">
        <h2 className="font-display text-2xl mb-2">When does Pro pay for itself?</h2>
        <p className="text-sm text-muted-foreground mb-6">A quick sanity check — Pro starts saving you money once your monthly sales pass these thresholds.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="p-5 bg-[#F3EFE9] rounded-lg">
            <div className="label-overline text-muted-foreground mb-1">Products (8% → 5%)</div>
            <div className="font-display text-3xl">~{sym}{Math.round(userPrice / 0.03)}/mo</div>
            <div className="text-xs text-muted-foreground mt-1">break-even in sales</div>
          </div>
          <div className="p-5 bg-[#F3EFE9] rounded-lg">
            <div className="label-overline text-muted-foreground mb-1">Services (10% → 5%)</div>
            <div className="font-display text-3xl">~{sym}{Math.round(userPrice / 0.05)}/mo</div>
            <div className="text-xs text-muted-foreground mt-1">break-even in sales</div>
          </div>
          <div className="p-5 bg-[#C05A3C]/10 rounded-lg border border-[#C05A3C]/20">
            <div className="label-overline text-[#C05A3C] mb-1">Plus the badge ({sym}{pub?.verified_badge_price ?? 19.99})</div>
            <div className="font-display text-3xl">≈ Day 1</div>
            <div className="text-xs text-muted-foreground mt-1">if you&apos;d buy the badge anyway</div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Pro;
